// Per-browser "seen" state for the Admin diagnostic-FAQ tip banner pinned at
// the top of Global Search's empty-query dropdown (see index.js). Local
// only, no backend - same storage convention as recentSearchStore.js /
// favoritesStore.js: scoped per company + user so a shared/public browser
// doesn't leak one admin's dismiss/view state to whoever logs in next on the
// same machine, and every localStorage access is wrapped in try/catch for
// private-browsing safety.
//
// The banner stops showing permanently once EITHER the admin explicitly
// dismissed it (click the x) OR it's been shown this many times already -
// at that point the admin has had every reasonable chance to notice the
// feature exists, so it should stop nagging rather than show forever.
import { getGlobalSearchKeySuffix } from "./globalSearchStorageKey";

const MAX_VIEWS = 5;

const getStorageKey = () => `globalSearch.diagnosticTipSeenCount.${getGlobalSearchKeySuffix()}`;

const DEFAULT_STATE = { count: 0, dismissed: false };

export const getDiagnosticTipState = () => {
  try {
    const raw = localStorage.getItem(getStorageKey());
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return { ...DEFAULT_STATE };
    return { count: Number(parsed.count) || 0, dismissed: !!parsed.dismissed };
  } catch (e) {
    return { ...DEFAULT_STATE };
  }
};

// Whether the banner is still allowed to render at all - false once
// explicitly dismissed, or once it's reached MAX_VIEWS opens.
export const isDiagnosticTipEligible = () => {
  const { count, dismissed } = getDiagnosticTipState();
  return !dismissed && count < MAX_VIEWS;
};

// Bumps the view count by one - called once per dropdown "open" while the
// tip is still eligible (not once per keystroke/re-render). Returns the
// resulting state so the caller can sync its own React state without a
// second read.
export const recordDiagnosticTipView = () => {
  try {
    const current = getDiagnosticTipState();
    if (current.dismissed || current.count >= MAX_VIEWS) return current;
    const next = { ...current, count: current.count + 1 };
    localStorage.setItem(getStorageKey(), JSON.stringify(next));
    return next;
  } catch (e) {
    // Private browsing / storage blocked - fail soft, same as every other
    // store in this module.
    return getDiagnosticTipState();
  }
};

export const dismissDiagnosticTip = () => {
  try {
    const current = getDiagnosticTipState();
    const next = { ...current, dismissed: true };
    localStorage.setItem(getStorageKey(), JSON.stringify(next));
    return next;
  } catch (e) {
    return { ...getDiagnosticTipState(), dismissed: true };
  }
};
